import { useState } from 'react';

export default function ApplicationForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    occupation: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        instagram: '',
        tiktok: '',
        linkedin: '',
        occupation: ''
      });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="application" className="bg-black px-6 py-32">
      <div className="mx-auto max-w-3xl">
        <div className="mb-16 text-center">
          <div className="mb-6 h-px w-24 mx-auto bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
          <h2 className="mb-6 font-serif text-4xl font-light text-white md:text-5xl lg:text-6xl">
            Ready to <span className="italic">Apply?</span>
          </h2>
          <p className="text-lg text-gray-400">
            Membership is by application only. We review each submission carefully and accept only those who meet our standards.
          </p>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-8 border border-amber-400 bg-amber-400/10 p-6 text-center">
            <i className="ri-checkbox-circle-line mb-3 text-4xl text-amber-400"></i>
            <p className="text-lg font-medium text-white">Application Submitted Successfully!</p>
            <p className="mt-2 text-sm text-gray-400">We'll review your application and get back to you within 3-5 business days.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="border border-gray-800 bg-gradient-to-br from-gray-900 to-black p-8 md:p-12">
          <div className="mb-8 grid gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="fullName" className="mb-2 block text-sm font-medium text-gray-300">
                Your Name <span className="text-amber-400">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
                placeholder="Name"
              />
            </div>

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-300">
                Email <span className="text-amber-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="mb-8 grid gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="instagram" className="mb-2 block text-sm font-medium text-gray-300">
                Instagram Handle <span className="text-amber-400">*</span>
              </label>
              <input
                type="text"
                id="instagram"
                name="instagram"
                value={formData.instagram}
                onChange={handleChange}
                required
                className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
                placeholder="@yourusername"
              />
            </div>

            <div>
              <label htmlFor="tiktok" className="mb-2 block text-sm font-medium text-gray-300">
                TikTok Handle <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                id="tiktok"
                name="tiktok"
                value={formData.tiktok}
                onChange={handleChange}
                className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
                placeholder="@username"
              />
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="linkedin" className="mb-2 block text-sm font-medium text-gray-300">
              LinkedIn Profile <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              type="text"
              id="linkedin"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleChange}
              className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
              placeholder="linkedin.com/in/username"
            />
          </div>

          <div className="mb-8">
            <label htmlFor="occupation" className="mb-2 block text-sm font-medium text-gray-300">
              Occupation / Industry <span className="text-amber-400">*</span>
            </label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              required
              className="w-full border border-gray-700 bg-black px-4 py-3 text-sm text-white transition-colors focus:border-amber-400 focus:outline-none"
              placeholder="e.g., Model, Entrepreneur, Influencer"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-amber-400 px-8 py-4 text-base font-medium tracking-widest text-black transition-all duration-300 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
          </button>

          <p className="mt-6 text-center text-sm text-gray-500">
            Only qualified applicants will be contacted.
          </p>
        </form>

        <div className="mt-12 border-l-4 border-amber-400 bg-gray-900 p-8">
          <p className="text-lg text-gray-300">
            <span className="font-medium text-white">What happens next?</span> Our team reviews every application carefully. If you're selected, you'll receive an invitation to join Clique within 3-5 business days.
          </p>
        </div>
      </div>
    </section>
  );
}
